<template>
  <div :class="['bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 flex flex-row items-center gap-4 relative border border-gray-100 dark:border-gray-700 cursor-pointer group hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98] transition-all duration-200', showMenu ? 'z-50' : 'z-0']" 
  @click="navigateToPlaylist">
    
    <!-- Thumbnail / Icon -->
    <div class="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900 dark:to-purple-900 flex items-center justify-center shadow-inner">
        <span class="text-indigo-600 dark:text-indigo-300 font-bold text-lg">
            {{ props.playlist.name ? props.playlist.name.charAt(0).toUpperCase() : 'P' }}
        </span>
    </div>

    <!-- Info -->
    <div class="flex flex-col flex-grow min-w-0">
      <div class="flex items-center gap-2">
        <span class="text-lg font-semibold text-gray-800 dark:text-gray-100 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors truncate">{{ props.playlist.name }}</span>
        <span v-if="isOffline" class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 shrink-0">
            <CloudArrowDownIcon class="w-3 h-3" />
            <span>Offline</span>
        </span>
      </div>
      <div class="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-xs font-medium text-gray-400 dark:text-gray-500">
        <span class="whitespace-nowrap">{{ $t('playlist.songsCount', { count: props.playlist.songs.length }) }}</span>
        <span class="hidden sm:inline">•</span>
        <span class="truncate">{{ $t('playlist.createdAt', { date: formatDate(props.playlist.created_at) }) }}</span>
      </div>
    </div>

    <!-- Actions Menu -->
    <div class="relative" ref="menuRef">
      <button class="p-2 rounded-full text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-gray-700 transition-colors" 
        @click.stop="toggleMenu">
        <EllipsisVerticalIcon class="w-5 h-5" />
      </button>

      <!-- Dropdown -->
      <transition enter-active-class="transition duration-100 ease-out" enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100" leave-active-class="transition duration-75 ease-in" leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0">
        <div v-if="showMenu" class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-100 dark:border-gray-700 z-50 py-1" @click.stop>
           <!-- Edit / View -->
           <button class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2" @click="editPlaylistAndClose">
              <PencilIcon class="w-4 h-4 text-gray-400" v-if="props.playlist.permissions && props.playlist.permissions.includes('editor')"/>
              <EyeIcon class="w-4 h-4 text-gray-400" v-else/>
              {{ props.playlist.permissions && props.playlist.permissions.includes('editor') ? $t('playlist.edit') : $t('playlist.view') }}
           </button>
           <!-- Open in Studio (all users with access) -->
           <button class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2" @click="openInStudioAndClose">
              <AdjustmentsHorizontalIcon class="w-4 h-4 text-violet-500" />
              {{ $t('playlistView.openInStudio') }}
           </button>
           <!-- Favorite -->
           <button class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2" @click="toggleFavoriteAndClose">
              <StarIcon class="w-4 h-4" :class="props.playlist.isFavorite ? 'text-yellow-400' : 'text-gray-400'" />
              {{ props.playlist.isFavorite ? $t('playlist.removeFavorite') : $t('playlist.addFavorite') }}
           </button>
           <!-- Offline Toggle -->
           <button class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2" @click="toggleOfflineAndClose">
              <CloudArrowDownIcon class="w-4 h-4" :class="isOffline ? 'text-emerald-500' : 'text-gray-400'" />
              {{ isOffline ? ($t('playlistView.removeOffline') || 'Remover do dispositivo') : ($t('playlistView.saveOffline') || 'Salvar Offline') }}
           </button>
        </div>
      </transition>
    </div>

    <!-- Loading Spinner Overlay -->
    <div v-if="props.updatingFavorite" class="absolute inset-0 bg-white/50 dark:bg-gray-800/50 flex items-center justify-center rounded-xl z-40">
        <Loading size="!w-6 !h-6"></Loading>
    </div>
  </div>
</template>
<script setup>
import { defineProps, defineEmits, ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { PencilIcon, EyeIcon, EllipsisVerticalIcon, StarIcon, CloudArrowDownIcon, AdjustmentsHorizontalIcon } from '@heroicons/vue/24/outline'
import { openPlaylistInStudio } from '@/utils/studio'
import offlinePlaylistService from '@/service/offlinePlaylist.service'
import { appEvents } from '@/store/app.events'
import toastStore from '@/store/toast.store'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  playlist: Object,
  updatingFavorite: Boolean
})

const router = useRouter()
const emit = defineEmits(['toggle-favorite'])

const showMenu = ref(false)
const menuRef = ref(null)
const isOffline = ref(false)

const toggleMenu = (evt) => {
    evt.stopPropagation()
    showMenu.value = !showMenu.value
}

const onClickOutside = (event) => {
    if (menuRef.value && !menuRef.value.contains(event.target)) {
        showMenu.value = false
    }
}

const checkOfflineStatus = async () => {
    const id = props.playlist?._id || props.playlist?.id
    if (!id) return
    isOffline.value = await offlinePlaylistService.isPlaylistDownloaded(id)
}

const onOfflineEvent = () => {
    checkOfflineStatus()
}

onMounted(() => {
    document.addEventListener('click', onClickOutside)
    checkOfflineStatus()
    appEvents.on('offline:playlist-downloaded', onOfflineEvent)
    appEvents.on('offline:playlist-removed', onOfflineEvent)
})

onBeforeUnmount(() => {
    document.removeEventListener('click', onClickOutside)
    appEvents.off('offline:playlist-downloaded', onOfflineEvent)
    appEvents.off('offline:playlist-removed', onOfflineEvent)
})

const toggleOfflineAndClose = async (evt) => {
    evt.stopPropagation()
    showMenu.value = false
    const id = props.playlist?._id || props.playlist?.id
    if (!id) return

    if (isOffline.value) {
        try {
            await offlinePlaylistService.removePlaylist(id)
            isOffline.value = false
            toastStore.dispatch('addMessage', { message: t('playlistView.removeSuccess') || 'Playlist removida do dispositivo.', icon: 'success' })
        } catch (e) {
            console.error('Error removing offline playlist:', e)
        }
    } else {
        try {
            toastStore.dispatch('addMessage', { message: t('menu.offlineSyncing', { current: 0, total: props.playlist.songs?.length || 0 }) || 'Baixando...', icon: 'info' })
            await offlinePlaylistService.downloadPlaylist(props.playlist)
            isOffline.value = true
            toastStore.dispatch('addMessage', { message: t('playlistView.downloadSuccess') || 'Playlist salva offline!', icon: 'success' })
        } catch (e) {
            console.error('Error downloading offline playlist:', e)
            if (e.code === 'FREE_LIMIT_REACHED' || e.message === 'FREE_LIMIT_REACHED') {
                toastStore.dispatch('addMessage', {
                    message: t('playlistView.offlineFreeLimitReached') || 'Usuários do plano gratuito podem salvar apenas 1 playlist offline.',
                    icon: 'warning'
                })
            } else {
                toastStore.dispatch('addMessage', { message: 'Erro ao baixar playlist offline', icon: 'error' })
            }
        }
    }
}

const toggleFavoriteAndClose = (evt) => {
  evt.stopPropagation()
  showMenu.value = false
  emit('toggle-favorite', props.playlist)
}

function navigateToPlaylist() {
  const playlistId = props.playlist._id || props.playlist.id;
  if (props.playlist?.songs.length){
    const song = props.playlist.songs[0]
    const id = song?.song || song?.song_id || song?.data?._id || song?.userData?._id
    router.push( {path: `/playlist/${playlistId}/song/${id}`});
  } else {
    router.push( {path: `/playlist/${playlistId}`})
  }
}

const formatDate = (date) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric'}
    return new Date(date).toLocaleDateString('en', options)
}

const openInStudioAndClose = (evt) => {
  evt.stopPropagation()
  showMenu.value = false
  openPlaylistInStudio(props.playlist._id || props.playlist.id)
}

const editPlaylistAndClose = (evt) => {
  evt.stopPropagation()
  showMenu.value = false
  const playlistId = props.playlist._id || props.playlist.id;
  router.push( {path: `/playlist/${playlistId}`})
}
</script> 